<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { folderKey } from "@/ui/components/FileBrowser/types";
import { FileScope } from "@/ui/components/FileBrowser/useFileTable";
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;

const props = defineProps<{
	path: ResourceTreeNodeDO[];
	search: string;
	scope: FileScope;
	hasActiveFilters: boolean;
	total: number;
	isDropTarget: (folder: ResourceTreeNodeDO) => boolean;
}>();

const emit = defineEmits<{
	navigate: [folder: ResourceTreeNodeDO];
	up: [];
	"update:search": [value: string];
	"update:scope": [scope: FileScope];
	clear: [];
	"drag-over-crumb": [event: DragEvent, folder: ResourceTreeNodeDO];
	"drag-leave-crumb": [folder: ResourceTreeNodeDO];
	"drop-on-crumb": [event: DragEvent, folder: ResourceTreeNodeDO];
}>();

/**
 * Local mirror of the search text so typing stays responsive while the request
 * is debounced. One request per 350ms of quiet, not one per keystroke.
 */
const draft = ref(props.search);
let debounce: ReturnType<typeof setTimeout> | null = null;

watch(
	() => props.search,
	(value) => {
		if (value !== draft.value) {
			draft.value = value;
		}
	}
);

const onInput = (value: string): void => {
	draft.value = value;

	if (debounce) {
		clearTimeout(debounce);
	}

	debounce = setTimeout(() => emit("update:search", value), 350);
};

onBeforeUnmount(() => {
	if (debounce) {
		clearTimeout(debounce);
	}
});

const { t } = useI18n();

const SCOPES: { value: FileScope; labelKey: string }[] = [
	{ value: "global", labelKey: "fileBrowser.scope.global" },
	{ value: "folder", labelKey: "fileBrowser.scope.folder" },
];
</script>

<template>
	<div class="address-bar">
		<!-- Sits over the folder tree, and is exactly as wide as it. -->
		<div class="address-bar__aside">
			<v-text-field
				:model-value="draft"
				:placeholder="t('fileBrowser.search')"
				prepend-inner-icon="fa: fa-solid fa-magnifying-glass"
				density="compact"
				variant="solo-filled"
				flat
				hide-details
				clearable
				class="search"
				@update:model-value="onInput"
				@click:clear="onInput('')"
			/>
		</div>

		<!-- Sits over the file table: where you are, and what is filtering it. -->
		<div class="address-bar__path">
			<v-btn
				icon
				size="small"
				variant="text"
				density="comfortable"
				:disabled="path.length <= 1"
				@click="emit('up')"
			>
				<v-icon size="13">fa: fa-solid fa-chevron-left</v-icon>
				<v-tooltip activator="parent">
					{{ t("fileBrowser.upOneFolder") }}
				</v-tooltip>
			</v-btn>

			<div class="crumbs" :class="{ 'crumbs--inactive': scope === 'global' }">
				<template v-for="(folder, index) in path" :key="folderKey(folder)">
					<v-icon v-if="index > 0" size="8" class="crumbs__sep">
						fa: fa-solid fa-chevron-right
					</v-icon>
					<!-- Also a drop target, which is how a drag moves something up:
					     the ancestors are not otherwise on screen. -->
					<button
						type="button"
						class="crumb"
						:class="{
							'crumb--current': index === path.length - 1,
							'crumb--drop-target': isDropTarget(folder),
						}"
						@click="emit('navigate', folder)"
						@dragenter="emit('drag-over-crumb', $event, folder)"
						@dragover="emit('drag-over-crumb', $event, folder)"
						@dragleave="emit('drag-leave-crumb', folder)"
						@drop="emit('drop-on-crumb', $event, folder)"
					>
						{{ folder.name }}
					</button>
				</template>
			</div>

			<v-spacer />

			<v-chip
				v-if="hasActiveFilters"
				size="small"
				closable
				class="mr-2"
				@click:close="emit('clear')"
			>
				{{ t("fileBrowser.matchCount", total) }}
			</v-chip>

			<v-menu v-if="hasActiveFilters">
				<template #activator="{ props: menuProps }">
					<v-btn
						v-bind="menuProps"
						variant="text"
						size="small"
						append-icon="fa: fa-solid fa-caret-down"
						class="mr-1"
					>
						{{ t(`fileBrowser.scope.${scope}`) }}
					</v-btn>
				</template>
				<v-list density="compact" min-width="150">
					<v-list-item
						v-for="option in SCOPES"
						:key="option.value"
						:active="option.value === scope"
						:title="t(option.labelKey)"
						@click="emit('update:scope', option.value)"
					/>
				</v-list>
			</v-menu>
		</div>
	</div>
</template>

<style scoped lang="scss">
.address-bar {
	display: flex;
	align-items: center;
	min-width: 0;

	/*
	 * --tree-width comes from .file-explorer. Custom properties cascade through
	 * scoped styles, so the toolbar and the body below it cannot drift apart.
	 */
	&__aside {
		flex: 0 0 var(--tree-width);
		min-width: 0;
		padding: 0 8px;
	}

	&__path {
		display: flex;
		align-items: center;
		gap: 2px;
		flex: 1 1 auto;
		min-width: 0;
	}
}

.crumbs {
	display: flex;
	align-items: center;
	min-width: 0;
	overflow: hidden;
	margin-left: 2px;

	&--inactive {
		opacity: 0.45;
		text-decoration: line-through;
	}

	&__sep {
		margin: 0 3px;
		opacity: 0.4;
		flex: none;
	}
}

.crumb {
	padding: 1px 4px;
	border-radius: 4px;
	font-size: 12.5px;
	white-space: nowrap;
	color: rgb(var(--color-on-background));

	&:hover {
		background-color: rgba(var(--color-secondary), 0.7);
	}

	&--current {
		font-weight: 600;
		color: rgb(var(--color-on-surface));
	}

	&--drop-target {
		outline: 2px solid rgb(var(--color-primary));
		outline-offset: -1px;
		background-color: rgba(var(--color-primary), 0.12);
	}
}

.search {
	width: 100%;

	:deep(.v-field) {
		font-size: 13px;
		border-radius: 5px;
	}
}
</style>
